/* eslint-disable no-console */
import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { useParams, NavLink } from 'react-router-dom'
import {
  JsonView,
  DescriptionTable,
  StatusCard,
  TooltipHeader,
  IconClipboard
} from 'shared/components'
import { defaultDateFormat, licenseTypes } from 'shared/constants'
import {
  sendErrorReport,
  displayValue,
  formatDate,
  copyText,
  capitalizeFirstLetter,
  displayMaxOveragesValue
} from 'shared/helpers'
import { checkCompanyConfigField } from 'shared/companyConfig'
import { fetchLicense } from 'src/license/actions'
import './styles.scss'

export default function LicensesTab({ licenseID }) {
  const { companyId } = useParams()
  const [license, setLicense] = useState(null)
  // const [licenseLoading, setLicenseLoading] = useState(true)
  const currentProduct = get(license, 'product', {})
  const productID = get(currentProduct, 'id')
  const productName = get(currentProduct, 'product_name')
  const licenseStatusText = get(license, 'status')
  const licenseStatus = licenseStatusText === 'Active' ? 'success' : 'error'
  const isTrial = get(license, 'is_trial')
  const isConsumption = get(license, 'license_type') === licenseTypes.consumption
  const isSubscription = get(license, 'license_type') === licenseTypes.subscription
  const allowGracePeriod = get(license, 'allow_grace_period')
  const gracePeriod = get(license, 'grace_period')
  const isFloatingLicense = get(license, 'is_floating') || get(license, 'is_floating_cloud')
  const canBorrow = get(license, 'can_borrow')
  const isEnabled = get(license, 'enabled')
  const licenseStartDate = get(license, 'start_date')
  const userGroup = get(license, 'license_user_group')
  const floatingTimeout = get(license, 'floating_timeout')

  const updateLicense = () => {
    // setLicenseLoading(true)
    fetchLicense(licenseID, companyId)
      .then(res => {
        setLicense(get(res, 'data'))
        // setLicenseLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get customer licenses')
        // setLicenseLoading(false)
      })
  }

  useEffect(() => {
    updateLicense()
  }, [])

  const handleFieldCopy = (val, desc) => {
    copyText(val)
    Notification('success', `${desc} ${__('copied to clipboard')}`)
  }

  return (
    <DescriptionTable
      details={[
        {
          label: __('ID'),
          value: (
            <div>
              <NavLink to={`/${companyId}/orders/${get(license, 'order')}/${licenseID}`}>
                {displayValue(licenseID)}
              </NavLink>
              <button
                type='button'
                className='clipboard-btn'
                onClick={() => handleFieldCopy(licenseID, 'License ID')}
              >
                <IconClipboard width='15' height='15' viewBox='0 0 51.5 50' />
              </button>
            </div>
          )
        },
        {
          label: __('Product'),
          value: (
            <NavLink to={`/${companyId}/products/${productID}`}>
              {displayValue(productName)}
            </NavLink>
          )
        },
        {
          label: __('License type'),
          value: displayValue(capitalizeFirstLetter(get(license, 'license_type')))
        },
        {
          label: userGroup ? __('User Group') : null,
          value: userGroup ? displayValue(userGroup.name) : null
        },
        {
          label: licenseStartDate
            ? TooltipHeader(__('Start Date'), __('License cannot be activated before this date'))
            : null,
          value: formatDate(licenseStartDate, defaultDateFormat)
        },
        {
          label: isEnabled ? TooltipHeader(__('Expiration Date')) : null,
          value: formatDate(get(license, 'validity_period'), defaultDateFormat)
        },
        {
          label: !isTrial ? __('Valid duration') : null,
          value: displayValue(get(license, 'valid_duration'))
        },
        {
          label: isSubscription && allowGracePeriod ? __('Grace period') : null,
          value: `${gracePeriod} ${__('hours')}`
        },
        {
          label: __('Status'),
          value: <StatusCard text={licenseStatusText} status={licenseStatus} />
        },
        { label: __('Created on'), value: formatDate(get(license, 'created_at')) },
        { label: __('Activated on'), value: formatDate(get(license, 'time_activated')) },
        { label: __('Last checked'), value: formatDate(get(license, 'last_check')) },
        { label: __('Last updated'), value: formatDate(get(license, 'updated_at')) },
        {
          label: isEnabled ? null : __('Disabled on'),
          value: formatDate(get(license, 'time_disabled'))
        },
        {
          label: checkCompanyConfigField(companyId, 'maxActivations')
            ? __('Max activations')
            : null,
          value: get(license, 'allow_unlimited_activations')
            ? __('Unlimited')
            : displayValue(get(license, 'max_activations'))
        },
        {
          label: __('Times activated'),
          value: displayValue(get(license, 'times_activated'), '0')
        },
        { label: __('Transfer count'), value: displayValue(get(license, 'transfer_count'), '0') },
        {
          label: get(license, 'max_transfers') ? __('Device transfer limit') : null,
          value:
            get(license, 'max_transfers') === -1
              ? __('Device transfer not allowed')
              : displayValue(get(license, 'max_transfers'))
        },
        {
          label: checkCompanyConfigField(companyId, 'isTrial') ? __('Is trial') : null,
          value: get(license, 'is_trial') ? __('Yes') : __('No')
        },
        {
          label:
            checkCompanyConfigField(companyId, 'isTrial') && isTrial && get(license, 'trial_days')
              ? __('Trial days')
              : null,
          value: displayValue(get(license, 'trial_days'))
        },
        // consumption license type data
        {
          label: isConsumption ? __('Max consumptions') : null,
          value: get(license, 'allow_unlimited_consumptions')
            ? __('Unlimited')
            : displayValue(get(license, 'max_consumptions'), '0')
        },
        {
          label: isConsumption ? __('Allow negative consumptions') : null,
          value: get(license, 'allow_negative_consumptions') ? __('Yes') : __('No')
        },
        {
          label: isConsumption ? __('Total consumptions') : null,
          value: () => {
            const value = displayValue(get(license, 'total_consumptions'), '0')
            return (
              <div className='total-consumptions-cell'>
                <div className='value'>{value}</div>
              </div>
            )
          }
        },
        {
          label: isConsumption ? __('Allow overages') : null,
          value: get(license, 'allow_overages') ? __('Yes') : __('No')
        },
        {
          label: isConsumption ? __('Max overages') : null,
          value: displayMaxOveragesValue(license)
        },
        {
          label: isConsumption ? __('Reset consumption') : null,
          value: get(license, 'reset_consumption') ? __('Yes') : __('No')
        },
        {
          label: isConsumption ? __('Consumption period') : null,
          value: displayValue(capitalizeFirstLetter(get(license, 'consumption_period')))
        },
        // floating licenses data
        {
          label: get(license, 'is_floating') ? __('Offline floating license') : null,
          value: get(license, 'is_floating') ? __('Yes') : __('No')
        },
        {
          label: get(license, 'is_floating_cloud') ? __('Is floating cloud') : null,
          value: get(license, 'is_floating_cloud') ? __('Yes') : __('No')
        },
        {
          label: isFloatingLicense ? __('Max simultaneous license users') : null,
          value: displayValue(get(license, 'floating_users'))
        },
        {
          label: isFloatingLicense ? __('Floating timeout') : null,
          value: `${displayValue(floatingTimeout)} min`
        },
        {
          label: get(license, 'is_floating_cloud') ? __('Floating in use') : null,
          value: `${get(license, 'floating_in_use_devices') || '0'} / ${get(
            license,
            'floating_users'
          )}`
        },
        {
          label: canBorrow ? __('Can borrow') : null,
          value: canBorrow ? __('Yes') : __('No')
        },
        {
          label: canBorrow ? __('Max borrow time') : null,
          value: `${displayValue(get(license, 'max_borrow_time'))} ${__('hours')}`
        },
        {
          label:
            get(license, 'enable_maintenance_period') && !get(license, 'maintenance_period')
              ? __('Maintenance duration')
              : null,
          value: displayValue(get(license, 'maintenance_duration'))
        },
        {
          label:
            get(license, 'enable_maintenance_period') && get(license, 'maintenance_period')
              ? __('Maintenance End Date')
              : null,
          value: formatDate(get(license, 'maintenance_period'), defaultDateFormat)
        },
        {
          label: __('Prevent virtual machine'),
          value: get(license, 'prevent_vm') ? __('Yes') : __('No')
        },
        { label: __('Note'), value: displayValue(get(license, 'note')) },
        {
          label: __('Metadata'),
          value: <JsonView value={get(license, 'metadata')} name='metadata' />
        }
      ]}
    />
  )
}
